import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ContractSignConfirm } from './ContractSignConfirm';
import { ContractSignaturePad } from './ContractSignaturePad';
import type { ContractClause } from '@/lib/contract-clauses';
import {
  clauseRef,
  clauseTitleBody,
  requiredClauses,
} from '@/lib/contract-clauses';
import { ESIGN_DISCLOSURE_CONSENT_LABEL } from '@/lib/esign-disclosure';
import { nextSigningNameInput, signingNameInputValue } from '@/lib/contract-signing';
import { htmlHasVisibleText, sanitizeHtml } from '@/lib/sanitize-html';
import './contract-document.css';

export type ClientSignStep = 'consent' | 'initials' | 'sign';

export function nextClientSignStep(
  accepted: boolean,
  required: ContractClause[],
  initials: Record<string, string>,
): ClientSignStep {
  if (!accepted) return 'consent';
  if (required.some((clause) => !initials[clause.id]?.trim())) return 'initials';
  return 'sign';
}

/** Compact sub-row: initials | ref + title | expand. */
export function ClauseInitialBox({
  clause,
  value,
  onChange,
  inputId,
  readOnly = false,
}: {
  clause: ContractClause;
  value: string;
  onChange?: (text: string) => void;
  inputId: string;
  readOnly?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const ref = clauseRef(clause.title);
  const title = clauseTitleBody(clause.title);
  const hasBody = htmlHasVisibleText(clause.html);

  return (
    <div
      className="cd-clause-initial-box"
      data-testid={`clause-initial-box-${clause.id}`}
      data-clause-id={clause.id}
    >
      <div className="cd-clause-initial-main">
        {readOnly ? (
          <span className="cd-clause-initial-value" aria-label={`Initials for ${clause.title}`}>
            {value.trim() || '—'}
          </span>
        ) : (
          <Input
            id={inputId}
            value={value}
            onChange={(event) => onChange?.(event.target.value)}
            placeholder="JL"
            maxLength={8}
            className="cd-initial-input"
            aria-label={`Initials for ${clause.title}`}
          />
        )}
        <div className="cd-clause-initial-meta">
          {ref ? <span className="cd-clause-ref">{ref}</span> : null}
          <span className="cd-clause-initial-title">{title || clause.title}</span>
        </div>
        {hasBody ? (
          <button
            type="button"
            className="cd-clause-expand"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            aria-label={expanded ? `Hide ${clause.title}` : `Read ${clause.title}`}
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>
        ) : (
          <span className="cd-clause-expand-spacer" aria-hidden />
        )}
      </div>
      {expanded && hasBody ? (
        <div
          className="cd-clause-initial-body"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(clause.html ?? '') }}
        />
      ) : null}
    </div>
  );
}

export function ContractClientSignSteps({
  partyKey,
  partyName,
  printedName,
  onPrintedNameChange,
  printedNameLabel,
  printedNamePlaceholder,
  signature,
  onSignatureChange,
  clauses,
  initials,
  onInitialChange,
  accepted,
  onAccepted,
  consentLabel = ESIGN_DISCLOSURE_CONSENT_LABEL,
  onSubmit,
  pending,
  testId,
  /** When true, initials are captured in Contract Terms (IV/V); this panel only advances. */
  initialsInDocument = true,
}: {
  partyKey: string;
  partyName: string;
  printedName: string;
  onPrintedNameChange: (next: string) => void;
  printedNameLabel: string;
  printedNamePlaceholder: string;
  signature: string;
  onSignatureChange: (next: string) => void;
  clauses: ContractClause[];
  initials: Record<string, string>;
  onInitialChange: (clauseId: string, text: string) => void;
  accepted: boolean;
  onAccepted: (next: boolean) => void;
  consentLabel?: string;
  onSubmit: () => void;
  pending?: boolean;
  testId?: string;
  initialsInDocument?: boolean;
}) {
  const required = useMemo(() => requiredClauses(clauses), [clauses]);
  const [phase, setPhase] = useState<ClientSignStep>('consent');
  const readyStep = nextClientSignStep(accepted, required, initials);
  useEffect(() => {
    if (readyStep === 'consent') setPhase('consent');
  }, [readyStep]);
  useEffect(() => {
    if (phase !== 'initials' || required.length === 0) return;
    const target = document.querySelector('[data-testid="contract-terms-print"]');
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [phase, required.length]);

  const step: ClientSignStep =
    readyStep === 'consent' || phase === 'consent'
      ? 'consent'
      : readyStep === 'initials' || phase === 'initials'
        ? 'initials'
        : 'sign';

  const allFilled = readyStep === 'sign';

  return (
    <div className="cd-client-steps" data-testid={testId ?? `${partyKey}-sign-steps`}>
      <div className="cd-sign-name">
        <Label htmlFor={`${partyKey}-printed-name`}>{printedNameLabel}</Label>
        <Input
          id={`${partyKey}-printed-name`}
          value={signingNameInputValue(printedName)}
          onChange={(event) =>
            onPrintedNameChange(
              nextSigningNameInput(printedName, event.target.value, printedNamePlaceholder),
            )
          }
          placeholder={printedNamePlaceholder}
          className="cd-sign-name-input text-card-foreground caret-[hsl(var(--card-foreground))]"
          autoComplete="name"
        />
      </div>
      {step === 'consent' ? (
        <ContractSignConfirm
          testId={`${partyKey}-sign-confirm`}
          includeDisclosure={false}
          checkboxId={`${partyKey}-consent`}
          accepted={accepted}
          onAccepted={onAccepted}
          label={consentLabel}
          primaryLabel={
            required.length > 0 ? 'Continue to initials' : 'Continue to signature'
          }
          primaryDisabled={!accepted}
          onPrimary={() => setPhase(required.length > 0 ? 'initials' : 'sign')}
        />
      ) : null}

      {step === 'initials' ? (
        <div className="cd-initials-step" data-testid={`${partyKey}-initials`}>
          {initialsInDocument ? (
            <p className="cd-sig-note">
              Initial the marked sections in Contract Terms above
              {required.some((c) => c.section === 'warranty') ? ' (and Warranty)' : ''}, then
              continue to the signature.
            </p>
          ) : (
            <div className="cd-clause-initial-list">
              {required.map((clause) => (
                <ClauseInitialBox
                  key={clause.id}
                  clause={clause}
                  value={initials[clause.id] ?? ''}
                  onChange={(text) => onInitialChange(clause.id, text)}
                  inputId={`${partyKey}-${clause.id}`}
                />
              ))}
            </div>
          )}
          <Button
            type="button"
            disabled={!allFilled}
            onClick={() => setPhase('sign')}
            data-testid={`${partyKey}-initials-continue`}
          >
            Continue to signature
          </Button>
        </div>
      ) : null}

      {step === 'sign' ? (
        <>
          <ContractSignaturePad
            partyName={printedName || partyName}
            printedName={printedName}
            value={signature}
            onChange={onSignatureChange}
          />
          <Button
            type="button"
            data-testid={`${partyKey}-final-confirm`}
            disabled={!signingNameInputValue(printedName) || !signature || pending}
            onClick={onSubmit}
          >
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <PenLine className="h-4 w-4 mr-1.5" />
            )}
            Confirm signature
          </Button>
        </>
      ) : null}
    </div>
  );
}
